import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";
import "./AfterBooking.css";

// ✅ Dynamic API base URL — works locally & on HTTPS domain
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.quote.softvolute.in";

const milestoneStages = [
  { label: "Projects at 10% Completion", min: 10, max: 49 },
  { label: "Projects at 50% Completion", min: 50, max: 89 },
  { label: "Projects at 40% Completion", min: 90, max: 100 },
];

// Financial year runs April → March. Index 0 = April ... 11 = March.
const FY_MONTH_NAMES = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

const QUARTER_LABELS = [
  "Q1 (Apr–Jun)",
  "Q2 (Jul–Sep)",
  "Q3 (Oct–Dec)",
  "Q4 (Jan–Mar)",
];

// Calendar date -> financial year it belongs to (e.g. Oct 2026 -> 2026)
const getFinancialYear = (date) =>
  date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;

// Calendar month (0 = Jan) -> index inside the financial year (0 = April)
const toFyMonthIndex = (calendarMonth) => (calendarMonth + 9) % 12;

// Calendar year of a month that belongs to the given financial year
const getFyMonthYear = (fy, fyMonthIndex) => fy + (fyMonthIndex >= 9 ? 1 : 0);

const formatFyLabel = (fy) => `FY ${fy}-${String(fy + 1).slice(-2)}`;

// Date-only values stay on the calendar date that was entered
const parsePaymentDate = (value) => {
  if (!value) return null;
  const text = String(value);
  const date = /^\d{4}-\d{2}-\d{2}/.test(text)
    ? new Date(`${text.slice(0, 10)}T00:00:00`)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Session cache: cards can render immediately when returning to this page.
const CACHE_KEY = "afterBookingFastCacheV1";
const readCache = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
    return cached &&
      Array.isArray(cached.projects) &&
      Array.isArray(cached.payments)
      ? cached
      : null;
  } catch {
    return null;
  }
};

const AfterBooking = ({ onSelectProject }) => {
  const [initialCache] = useState(readCache);
  const [projects, setProjects] = useState(initialCache?.projects || []);
  const [payments, setPayments] = useState(initialCache?.payments || []);
  const [clientDetailsMap, setClientDetailsMap] = useState(
    initialCache?.clientDetailsMap || {},
  );
  const [loadingProjects, setLoadingProjects] = useState(!initialCache);
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();
  const openProject = (project) => {
    if (typeof onSelectProject === "function") {
      onSelectProject(project, "/after-booking");
    }
    navigate("/project", { state: { project } });
  };

  // Role permissions for this page
  const canSeeRevenue = canAccess("afterBooking", "afterBookingShowRevenue");
  const canSeeMilestones = canAccess(
    "afterBooking",
    "afterBookingShowMilestones",
  );

  const [editingDate, setEditingDate] = useState(null);
  const [dateDraft, setDateDraft] = useState("");
  const [savingDate, setSavingDate] = useState(false);
  const canEditDates =
    canAccess("clientDetail", "edit") ||
    canAccess("clientDetail", "clientDetailEdit");
  const canSaveDates =
    canAccess("clientDetail", "create") ||
    canAccess("clientDetail", "clientDetailSave");

  // ------------------------------------------------------------------
  // Revenue filter: Year / Quarter / Month dropdowns
  // Year = financial year (April–March).
  // pickedFY null     -> latest year that has entries
  // quarterSel "all"  -> every quarter
  // monthSel null     -> automatic: the latest month that has entries
  //                      (or the whole quarter when a quarter is chosen)
  // monthSel "all"    -> all months of the selected year / quarter
  // ------------------------------------------------------------------
  const [pickedFY, setPickedFY] = useState(null);
  const [quarterSel, setQuarterSel] = useState("all");
  const [monthSel, setMonthSel] = useState(null);

  const today = new Date();
  const currentFY = getFinancialYear(today);

  // Every payment that has a valid date
  const datedPayments = useMemo(
    () =>
      payments
        .map((payment) => ({
          amount: Number(payment.amount || 0),
          date: parsePaymentDate(payment.date),
        }))
        .filter((payment) => payment.date),
    [payments],
  );

  // Only the years in which entries exist (newest first)
  const fyOptions = useMemo(() => {
    const years = new Set();
    datedPayments.forEach(({ date }) => years.add(getFinancialYear(date)));
    const list = [...years].sort((a, b) => b - a);
    return list.length ? list : [currentFY];
  }, [datedPayments, currentFY]);

  const effectiveFY =
    pickedFY !== null && fyOptions.includes(pickedFY) ? pickedFY : fyOptions[0];

  // Totals and entry counts for each month of the selected financial year
  const monthStats = useMemo(() => {
    const totals = Array(12).fill(0);
    const counts = Array(12).fill(0);
    datedPayments.forEach(({ amount, date }) => {
      if (getFinancialYear(date) !== effectiveFY) return;
      const index = toFyMonthIndex(date.getMonth());
      totals[index] += amount;
      counts[index] += 1;
    });
    return { totals, counts };
  }, [datedPayments, effectiveFY]);

  // Automatic month = the latest month that has entries in this year
  let defaultMonth = 0;
  let foundEntries = false;
  for (let i = 11; i >= 0; i -= 1) {
    if (monthStats.counts[i] > 0) {
      defaultMonth = i;
      foundEntries = true;
      break;
    }
  }
  if (!foundEntries && effectiveFY === currentFY) {
    defaultMonth = toFyMonthIndex(today.getMonth());
  }

  const effectiveMonth =
    monthSel !== null ? monthSel : quarterSel === "all" ? defaultMonth : "all";

  // Months shown in the Month dropdown (only that quarter's months if a quarter is chosen)
  const monthOptionIndexes =
    quarterSel === "all"
      ? FY_MONTH_NAMES.map((_, index) => index)
      : [0, 1, 2].map((offset) => quarterSel * 3 + offset);

  let rangeStart = 0;
  let rangeEnd = 12;
  let periodLabel = formatFyLabel(effectiveFY);

  if (effectiveMonth !== "all") {
    rangeStart = effectiveMonth;
    rangeEnd = effectiveMonth + 1;
    periodLabel = `${FY_MONTH_NAMES[effectiveMonth]} ${getFyMonthYear(effectiveFY, effectiveMonth)}`;
  } else if (quarterSel !== "all") {
    rangeStart = quarterSel * 3;
    rangeEnd = rangeStart + 3;
    periodLabel = `${QUARTER_LABELS[quarterSel]} · ${formatFyLabel(effectiveFY)}`;
  }

  const revenueAmount = monthStats.totals
    .slice(rangeStart, rangeEnd)
    .reduce((sum, value) => sum + value, 0);
  const entryCount = monthStats.counts
    .slice(rangeStart, rangeEnd)
    .reduce((sum, value) => sum + value, 0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const fetchData = async () => {
      try {
        // Both requests start together. Existing cards stay visible during refresh.
        const [projectRes, paymentRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/projects`, { signal: controller.signal }),
          axios.get(`${BASE_URL}/api/payments`, { signal: controller.signal }),
        ]);
        if (!active) return;

        const allProjects = Array.isArray(projectRes.data)
          ? projectRes.data
          : [];
        const allPayments = Array.isArray(paymentRes.data)
          ? paymentRes.data
          : [];
        const totals = new Map();
        for (const payment of allPayments) {
          const id = String(payment.projectId?._id || payment.projectId || "");
          totals.set(id, (totals.get(id) || 0) + Number(payment.amount || 0));
        }
        const filteredProjects = allProjects.filter(
          (project) =>
            (totals.get(String(project._id)) || 0) >= 1 && !project.isArchived,
        );

        setProjects(filteredProjects);
        setPayments(allPayments);
        setLoadError("");
        setLoadingProjects(false);

        // Store the essential card data before loading optional client details.
        const cached = {
          projects: filteredProjects,
          payments: allPayments,
          clientDetailsMap: readCache()?.clientDetailsMap || {},
        };
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(cached));
        } catch {}

        // Fetch details in small parallel batches, never blocking the cards.
        const details = { ...cached.clientDetailsMap };
        const BATCH_SIZE = 6;
        for (
          let i = 0;
          i < filteredProjects.length && active;
          i += BATCH_SIZE
        ) {
          const batch = filteredProjects.slice(i, i + BATCH_SIZE);
          const results = await Promise.allSettled(
            batch.map((project) =>
              axios.get(
                `${BASE_URL}/api/client-details/${encodeURIComponent(project.name)}`,
                { signal: controller.signal },
              ),
            ),
          );
          if (!active) return;
          results.forEach((result, index) => {
            if (result.status === "fulfilled") {
              const project = batch[index];
              details[project._id] =
                result.value.data?.clientDetails || result.value.data || {};
            }
          });
          setClientDetailsMap({ ...details });
          try {
            sessionStorage.setItem(
              CACHE_KEY,
              JSON.stringify({
                projects: filteredProjects,
                payments: allPayments,
                clientDetailsMap: details,
              }),
            );
          } catch {}
        }
      } catch (error) {
        if (!active || error.code === "ERR_CANCELED") return;
        console.error("Error loading projects:", error);
        setLoadError(
          "Unable to refresh projects. Showing saved data if available.",
        );
        setLoadingProjects(false);
      }
    };

    fetchData();
    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const toDateInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const saveProjectDate = async (project, field) => {
    if (!canEditDates || !canSaveDates || savingDate) return;
    setSavingDate(true);
    try {
      const current = clientDetailsMap[project._id] || {};
      const updated = { ...current, [field]: dateDraft };
      const response = await axios.post(`${BASE_URL}/api/client-details/save`, {
        projectName: project.name,
        clientDetails: updated,
      });
      const saved = response.data?.clientDetails || updated;
      setClientDetailsMap((previous) => ({
        ...previous,
        [project._id]: saved,
      }));
      try {
        const cached = readCache();
        if (cached)
          sessionStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              ...cached,
              clientDetailsMap: {
                ...cached.clientDetailsMap,
                [project._id]: saved,
              },
            }),
          );
      } catch {}
      setEditingDate(null);
    } catch (error) {
      console.error("Date update failed:", error?.response?.data || error);
      alert(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Date could not be saved.",
      );
    } finally {
      setSavingDate(false);
    }
  };

  const editableDate = (project, field, value) => {
    const editable = canEditDates && canSaveDates;
    const active =
      editingDate?.projectId === project._id && editingDate?.field === field;

    const startEditing = () => {
      if (!editable) return;
      setDateDraft(toDateInput(value));
      setEditingDate({ projectId: project._id, field });
    };

    if (active) {
      return (
        <span className="ab-date-edit">
          <input
            type="date"
            autoFocus
            value={dateDraft}
            disabled={savingDate}
            onChange={(event) => setDateDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveProjectDate(project, field);
              if (event.key === "Escape") setEditingDate(null);
            }}
          />
          <button
            type="button"
            disabled={savingDate}
            onClick={() => saveProjectDate(project, field)}
          >
            Save
          </button>
          <button
            type="button"
            disabled={savingDate}
            onClick={() => setEditingDate(null)}
          >
            Cancel
          </button>
        </span>
      );
    }

    return (
      <span className="ab-date">
        <span
          className={`ab-date-value${editable ? " is-editable" : ""}`}
          onDoubleClick={startEditing}
          title={editable ? "Double-click to edit date" : undefined}
        >
          {value ? new Date(value).toLocaleDateString("en-IN") : "N/A"}
        </span>

        {/* Double-click does not work on touch screens, so show an edit button */}
        {editable && (
          <button
            type="button"
            className="ab-date-pencil"
            onClick={startEditing}
            aria-label="Edit date"
          >
            ✎
          </button>
        )}
      </span>
    );
  };

  // First recorded positive income date is the automatic project start date.
  // Use the payment's entered date, not its createdAt timestamp.
  const getProjectStart = (project) => {
    const projectId = String(project._id || "");
    const projectName = String(project.name || "")
      .trim()
      .toLowerCase();
    let earliest = null;

    for (const payment of payments) {
      if (Number(payment.amount || 0) <= 0) continue;
      const paymentId = String(
        payment.projectId?._id || payment.projectId || "",
      );
      const paymentName = String(payment.projectName || "")
        .trim()
        .toLowerCase();
      const belongsToProject = paymentId
        ? paymentId === projectId
        : Boolean(projectName && paymentName === projectName);
      if (!belongsToProject || !payment.date) continue;

      // Date-only values should remain on the selected calendar date in India.
      const dateText = String(payment.date);
      const date = /^\d{4}-\d{2}-\d{2}/.test(dateText)
        ? new Date(`${dateText.slice(0, 10)}T00:00:00`)
        : new Date(payment.date);
      if (Number.isNaN(date.getTime())) continue;
      if (!earliest || date.getTime() < earliest.getTime()) earliest = date;
    }
    return earliest;
  };

  const getProjectAging = (start) => {
    if (!start) return "N/A";
    const now = new Date();
    const from = Date.UTC(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );
    const to = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    if (from > to) return "Not started";
    return `${Math.floor((to - from) / 86400000)} days`;
  };

  const getTotalPaidForProject = (projectId) =>
    payments
      .filter(
        (p) => String(p.projectId?._id || p.projectId) === String(projectId),
      )
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const getProjectStage = (project) => {
    const paid = getTotalPaidForProject(project._id);
    const total = Number(project.finalAmount || project.quoteAmount || 1);
    const percent = Math.round((paid / total) * 100);
    for (let stage of milestoneStages) {
      if (percent >= stage.min && percent <= stage.max) {
        return stage.label;
      }
    }
    return null;
  };

  const groupedProjects = (label) =>
    projects.filter((p) => getProjectStage(p) === label);

  return (
    <div className="ab-page">
      <div className="ab-header">
        <button type="button" className="ab-back" onClick={() => navigate(-1)}>
          ⬅ Back
        </button>

        <h2 className="ab-title">🔄 Active Projects by Completion Milestone</h2>

        <div className="ab-header-spacer" />
      </div>

      {!canSeeRevenue && !canSeeMilestones && (
        <div className="ab-empty" role="alert">
          You do not have permission to view this page's data.
        </div>
      )}

      {canSeeRevenue && (
        <div className="ab-revenue">
          <div className="ab-rev-main">
            <h3>📈 Revenue Achieved</h3>
            <p>
              {periodLabel} · {entryCount}{" "}
              {entryCount === 1 ? "entry" : "entries"}
            </p>
            <strong className="ab-revenue-amount">
              ₹ {revenueAmount.toLocaleString("en-IN")}
            </strong>
          </div>

          <div className="ab-rev-filter">
            <label className="ab-field">
              <span>Year</span>
              <select
                value={effectiveFY}
                onChange={(e) => {
                  setPickedFY(Number(e.target.value));
                  setQuarterSel("all");
                  setMonthSel(null);
                }}
              >
                {fyOptions.map((fy) => (
                  <option key={fy} value={fy}>
                    {formatFyLabel(fy)}
                  </option>
                ))}
              </select>
            </label>

            <label className="ab-field">
              <span>Quarter</span>
              <select
                value={String(quarterSel)}
                onChange={(e) => {
                  const value = e.target.value;
                  setQuarterSel(value === "all" ? "all" : Number(value));
                  setMonthSel(null);
                }}
              >
                <option value="all">All Quarters</option>
                {QUARTER_LABELS.map((label, index) => (
                  <option key={label} value={index}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="ab-field">
              <span>Month</span>
              <select
                value={
                  effectiveMonth === "all" ? "all" : String(effectiveMonth)
                }
                onChange={(e) => {
                  const value = e.target.value;
                  setMonthSel(value === "all" ? "all" : Number(value));
                }}
              >
                <option value="all">All Months</option>
                {monthOptionIndexes.map((index) => (
                  <option key={index} value={index}>
                    {FY_MONTH_NAMES[index]} {getFyMonthYear(effectiveFY, index)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}

      {canSeeMilestones && loadingProjects && (
        <div className="ab-empty">Loading projects...</div>
      )}
      {canSeeMilestones && loadError && (
        <div className="ab-empty" role="alert">
          {loadError}
        </div>
      )}
      {canSeeMilestones && (
        <div className="ab-columns">
          {milestoneStages.map((stage) => {
            const stageProjects = groupedProjects(stage.label);

            return (
              <div key={stage.label} className="ab-column">
                <h3 className="ab-column-title">{stage.label}</h3>

                {!loadingProjects && !stageProjects.length && (
                  <div className="ab-empty">No projects in this stage</div>
                )}

                {stageProjects.map((proj) => {
                  const totalPaid = getTotalPaidForProject(proj._id);
                  const totalAmount = Number(
                    proj.finalAmount || proj.quoteAmount || 0,
                  );
                  const percentComplete =
                    totalAmount > 0
                      ? Math.round((totalPaid / totalAmount) * 100)
                      : 0;
                  const client = clientDetailsMap[proj._id] || {};
                  const projectStart = getProjectStart(proj);

                  return (
                    <div
                      key={proj._id}
                      className="ab-card"
                      role="link"
                      tabIndex={0}
                      title={`Open ${proj.name}`}
                      style={{ cursor: "pointer" }}
                      onClick={(event) => {
                        if (
                          event.target.closest(
                            "button, input, select, textarea, .ab-date",
                          )
                        )
                          return;
                        openProject(proj);
                      }}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openProject(proj);
                        }
                      }}
                    >
                      <div className="ab-card-head">{proj.name}</div>

                      <div className="ab-card-body">
                        <div className="ab-row">
                          <strong>Project Value:</strong>
                          <span style={{ fontWeight: "bold" }}>
                            ₹ {(totalAmount / 100000).toFixed(2)}L
                          </span>
                        </div>

                        <div className="ab-row">
                          <strong>Collected:</strong>
                          <span>₹ {totalPaid.toLocaleString("en-IN")}</span>
                        </div>

                        <div className="ab-row">
                          <strong>Project Start Date:</strong>
                          <span title="Automatically taken from first income date">
                            {projectStart
                              ? projectStart.toLocaleDateString("en-IN")
                              : "N/A"}
                          </span>
                        </div>

                        <div className="ab-row">
                          <strong>Project Aging:</strong>
                          <span>{getProjectAging(projectStart)}</span>
                        </div>

                        <div className="ab-row">
                          <strong>Expected Completion:</strong>
                          {editableDate(
                            proj,
                            "expectedCompletionDate",
                            client.expectedCompletionDate,
                          )}
                        </div>

                        <div className="ab-row">
                          <strong>Completion:</strong>
                          <span
                            style={{
                              color:
                                percentComplete >= 100 ? "#08723E" : "#F39C12",
                              fontWeight: 600,
                            }}
                          >
                            {percentComplete}%
                          </span>
                        </div>

                        <div className="ab-badges">
                          <span className="ab-badge">
                            Softvolute Buildcon LLP
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AfterBooking;
